import type { User } from "firebase/auth";
import { useEffect, useState } from "react";
import * as api from "./api";
import { coordinate, hereNow, historyAccuracy, historyPoint, historySource, historyWhen, restoreOf } from "./location";
import { personName } from "./requests";
import { Skeleton } from "./Skeleton";

type Found = { history: api.PinEntry[]; can_restore: boolean } | "loading" | "failed";

/** Spec 5.7: the changes of the tank pin, newest first, each with a way to put the pin back. */
export function PinHistory({
  accountId,
  tank,
  canRestore,
  disabled,
  timeZone,
  user,
  onRestore,
}: {
  accountId: string;
  tank: api.StopTank | null | undefined;
  canRestore: boolean;
  disabled: boolean;
  timeZone: string;
  user: User;
  onRestore: (change: api.LocationChange, entry: api.PinEntry) => void;
}) {
  const [open, setOpen] = useState(false);
  const [found, setFound] = useState<Found>("loading");
  const [attempt, setAttempt] = useState(0);
  const tankKey = JSON.stringify(tank ?? null);

  useEffect(() => {
    if (!open) return;
    let live = true;
    setFound((was) => (typeof was === "object" ? was : "loading"));
    user
      .getIdToken()
      .then((token) => api.pinHistory(token, accountId))
      .then((got) => live && setFound(got))
      .catch(() => live && setFound("failed"));
    return () => {
      live = false;
    };
  }, [open, accountId, tankKey, attempt, user]);

  const restoring = canRestore && typeof found === "object" && found.can_restore;
  return (
    <details className="pin-history" data-testid="pin-history" onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary>Pin history</summary>
      {found === "loading" ? (
        <Skeleton rows={2} />
      ) : found === "failed" ? (
        <p role="alert" className="error" data-testid="pin-history-failed">
          The pin history cannot load.{" "}
          <button type="button" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </button>
        </p>
      ) : found.history.length === 0 ? (
        <p className="sub" data-testid="pin-history-empty">
          No changes to the container pin yet.
        </p>
      ) : (
        <div className="table-scroll">
          <table className="list pin-history-table">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">Who</th>
                <th scope="col">Source</th>
                <th scope="col">Accuracy</th>
                <th scope="col">Point</th>
                <th scope="col">
                  <span className="visually-hidden">Action</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {found.history.map((entry) => {
                const change = restoreOf(entry);
                const here = hereNow(entry, tank);
                const point = entry.lat !== null && entry.lng !== null ? `${coordinate(entry.lat)},${coordinate(entry.lng)}` : "";
                const restoreName = `Put back: ${historyPoint(entry)}, ${historyWhen(entry, timeZone)}`;
                return (
                  <tr key={entry.id} data-testid="pin-history-row" data-point={point} data-here={here || undefined}>
                    <td data-label="When">{historyWhen(entry, timeZone)}</td>
                    <td data-label="Who">
                      {entry.by ? (
                        <span className="clip name" title={entry.by}>
                          {personName(entry.by)}
                        </span>
                      ) : (
                        "–"
                      )}
                    </td>
                    <td data-label="Source">{historySource(entry)}</td>
                    <td data-label="Accuracy">{historyAccuracy(entry)}</td>
                    <td data-label="Point">{historyPoint(entry)}</td>
                    <td data-label="">
                      {here ? (
                        <span className="sub">Here now</span>
                      ) : (
                        change &&
                        restoring && (
                          <button
                            type="button"
                            className="pin-history-restore"
                            data-testid="pin-history-restore"
                            disabled={disabled}
                            aria-label={restoreName}
                            title={restoreName}
                            onClick={() => onRestore(change, entry)}
                          >
                            <span aria-hidden="true">↶</span>
                            <span className="pin-history-restore-text" aria-hidden="true">
                              {" "}
                              Put back
                            </span>
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </details>
  );
}
